import type { LucideIcon } from 'lucide-react';
import { LogOut, Moon, PanelLeftClose, PanelLeftOpen, Sun } from 'lucide-react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router';

import cyrinoIcon from '../assets/cyrino-icon.svg';
import cyrinoWordmark from '../assets/cyrino-wordmark.svg';
import cyrinoWordmarkOnDark from '../assets/cyrino-wordmark-on-dark.svg';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { cn } from '../lib/utils';
import { useTheme } from '../theme/theme-provider';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  permission?: string;
  end?: boolean;
  /** Items sharing a group render under their own heading, in the order the group first appears. Omit for the default "Main Menu" heading. */
  group?: string;
  /** An open-items count shown beside the label; nothing is rendered for zero or when omitted. */
  badge?: number;
}

interface SidebarProps {
  productName: string;
  sectionName: string;
  items: NavItem[];
  canAccess: (permission?: string) => boolean;
  collapsed: boolean;
  onToggle: () => void;
  fullName?: string;
  subtitle?: string;
  onSignOut?: () => void;
  /** Rendered below the account row — a build badge, an environment tag, and the like. */
  footer?: ReactNode;
  /** Extra items in the account dropdown, below the identity label — a role switcher, and the like. Keeps app-specific menu content out of this package. */
  accountMenuExtra?: ReactNode;
}

const initialsOf = (fullName: string): string =>
  fullName
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

interface SidebarBrandProps {
  productName: string;
  sectionName: string;
  collapsed: boolean;
}

function SidebarBrand({
  productName,
  sectionName,
  collapsed,
}: Omit<SidebarBrandProps, 'onToggle'>) {
  return (
    <div
      className={cn(
        'flex h-20 shrink-0 items-center transition-[padding] duration-150 ease-out',
        collapsed ? 'justify-center gap-0 px-3' : 'justify-start gap-2 px-6',
      )}
    >
      {/* Both marks stay mounted and cross-fade in place, rather than one
       * unmounting and the other popping in once the width settles. */}
      <img
        src={cyrinoIcon}
        alt={productName}
        className={cn(
          'h-10 shrink-0 object-contain transition-all duration-150 ease-out',
          collapsed ? 'w-10 scale-100 opacity-100' : 'w-0 scale-90 opacity-0',
        )}
      />

      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col items-start gap-1.5 overflow-hidden transition-all duration-150 ease-out',
          collapsed ? 'max-w-0 opacity-0' : 'max-w-[220px] opacity-100',
        )}
      >
        {/* The wordmark's lettering is navy, which vanishes on the dark rail,
         * so dark mode swaps in the white-lettered copy; the mark keeps its
         * colours in both. */}
        <img
          src={cyrinoWordmark}
          alt={productName}
          className="h-6 w-auto max-w-full object-contain object-left dark:hidden"
        />
        <img
          src={cyrinoWordmarkOnDark}
          alt={productName}
          className="hidden h-6 w-auto max-w-full object-contain object-left dark:block"
        />
        <span className="truncate text-[11px] font-medium leading-none text-sidebar-muted-foreground">
          {sectionName}
        </span>
      </div>
    </div>
  );
}

interface SidebarAccountProps {
  collapsed: boolean;
  fullName: string | undefined;
  subtitle: string | undefined;
  onSignOut?: () => void;
  accountMenuExtra?: ReactNode;
}

function SidebarAccount({
  collapsed,
  fullName,
  subtitle,
  onSignOut,
  accountMenuExtra,
}: SidebarAccountProps) {
  const { theme, toggleTheme } = useTheme();
  return (
    <div
      className={cn(
        'border-t border-sidebar-border py-3 transition-[padding] duration-150 ease-out',
        collapsed ? 'px-3' : 'px-4',
      )}
    >
      <div className="flex flex-col gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className={cn(
                'flex items-center rounded-md py-2 text-sm font-semibold text-sidebar-muted-foreground transition-colors duration-150 ease-out hover:bg-muted hover:text-sidebar-foreground',
                collapsed ? 'justify-center gap-0 px-2' : 'justify-start gap-3 px-3',
              )}
            >
              <Avatar className="h-6 w-6 shrink-0">
                <AvatarFallback className="bg-primary text-[10px] font-bold text-primary-foreground">
                  {fullName ? initialsOf(fullName) : String.fromCharCode(183, 183)}
                </AvatarFallback>
              </Avatar>
              <span
                className={cn(
                  'overflow-hidden truncate whitespace-nowrap text-left transition-all duration-150 ease-out',
                  collapsed ? 'max-w-0 opacity-0' : 'max-w-[160px] opacity-100',
                )}
              >
                {fullName ?? 'Signed out'}
              </span>
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="start" side="right" className="w-56 rounded-lg">
            <DropdownMenuLabel className="flex flex-col gap-0.5">
              <span>{fullName ?? 'Signed out'}</span>
              <span className="text-xs font-normal text-muted-foreground">
                {subtitle || String.fromCharCode(8212)}
              </span>
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            {accountMenuExtra}

            <DropdownMenuItem onSelect={toggleTheme}>
              {theme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              Switch to {theme === 'dark' ? 'light' : 'dark'} mode
            </DropdownMenuItem>

            {onSignOut ? (
              <DropdownMenuItem onSelect={onSignOut}>
                <LogOut className="h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

/**
 * Groups visible items by `group`, in the order each group first appears,
 * items without one falling under the default heading. A `Map` keeps
 * insertion order, so the default heading stays first as long as at least one
 * ungrouped item precedes the first grouped one — exactly how every existing
 * caller's item list is already ordered.
 */
const DEFAULT_NAV_GROUP_HEADING = 'Main Menu';

const groupNavItems = (items: NavItem[], defaultHeading: string): [string, NavItem[]][] => {
  const groups = new Map<string, NavItem[]>();

  for (const item of items) {
    const heading = item.group ?? defaultHeading;
    const existing = groups.get(heading);

    if (existing) existing.push(item);
    else groups.set(heading, [item]);
  }

  return [...groups.entries()];
};

interface SidebarNavLinkProps {
  item: NavItem;
  collapsed: boolean;
}

function SidebarNavBadge({ count, collapsed }: { count?: number; collapsed: boolean }) {
  if (!count) return null;

  return (
    <Badge
      variant="destructive"
      role="status"
      aria-label={`${count} open`}
      className={cn(
        'px-1.5 py-0 text-[11px] font-semibold leading-5',
        collapsed ? 'absolute right-1 top-0.5 min-w-4 px-1 leading-4' : 'ml-auto',
      )}
    >
      {count}
    </Badge>
  );
}

function SidebarNavLink({ item, collapsed }: SidebarNavLinkProps) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'group/nav relative flex items-center rounded-md py-2 text-sm font-semibold transition-colors duration-150 ease-out focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar',
          collapsed ? 'justify-center gap-0 px-2' : 'justify-start gap-3 px-3',
          isActive
            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
            : 'text-sidebar-muted-foreground hover:bg-muted hover:text-sidebar-foreground',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Active marker grows from the left edge rather than appearing,
           * so moving between items reads as the indicator travelling. */}
          <span
            aria-hidden
            className={cn(
              'absolute left-0 top-1/2 w-[3px] -translate-y-1/2 rounded-r bg-sidebar-primary transition-all duration-300 ease-out',
              isActive ? 'h-5 opacity-100' : 'h-0 opacity-0',
            )}
          />
          <item.icon className="h-[18px] w-[18px] shrink-0" />
          <span
            className={cn(
              'overflow-hidden truncate whitespace-nowrap transition-all duration-150 ease-out',
              collapsed ? 'max-w-0 opacity-0' : 'max-w-[160px] opacity-100',
            )}
          >
            {item.label}
          </span>
          <SidebarNavBadge count={item.badge} collapsed={collapsed} />
        </>
      )}
    </NavLink>
  );
}

export function Sidebar({
  productName,
  sectionName,
  items,
  canAccess,
  collapsed,
  onToggle,
  fullName,
  subtitle,
  onSignOut,
  footer,
  accountMenuExtra,
}: SidebarProps) {
  const visible = items.filter((item) => canAccess(item.permission));
  const groups = groupNavItems(visible, DEFAULT_NAV_GROUP_HEADING);
  const firstHeading = groups[0]?.[0] ?? DEFAULT_NAV_GROUP_HEADING;

  return (
    <aside
      className={cn(
        'hidden shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-150 ease-out lg:flex',
        collapsed ? 'w-20' : 'w-64',
      )}
    >
      <SidebarBrand productName={productName} sectionName={sectionName} collapsed={collapsed} />

      <nav
        className={cn(
          'flex flex-1 flex-col gap-1 overflow-y-auto py-4 transition-[padding] duration-150 ease-out',
          collapsed ? 'px-3' : 'px-4',
        )}
      >
        <div
          className={cn('mb-2 flex items-center', collapsed ? 'justify-center' : 'justify-between')}
        >
          {!collapsed && (
            <span className="px-3 text-caption uppercase text-sidebar-muted-foreground/80">
              {firstHeading}
            </span>
          )}
          {/* Collapse toggle aligned with the first heading's row. */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onToggle}
            aria-label={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}
            aria-expanded={!collapsed}
            className="shrink-0 text-sidebar-muted-foreground hover:bg-muted hover:text-sidebar-foreground"
          >
            {collapsed ? (
              <PanelLeftOpen className="h-[18px] w-[18px]" />
            ) : (
              <PanelLeftClose className="h-[18px] w-[18px]" />
            )}
          </Button>
        </div>
        {(groups[0]?.[1] ?? []).map((item) => (
          <SidebarNavLink key={item.to} item={item} collapsed={collapsed} />
        ))}

        {groups.slice(1).map(([heading, groupItems]) => (
          <div key={heading} className="mt-3 flex flex-col gap-1">
            {!collapsed && (
              <span className="mb-1 px-3 text-caption uppercase text-sidebar-muted-foreground/80">
                {heading}
              </span>
            )}
            {groupItems.map((item) => (
              <SidebarNavLink key={item.to} item={item} collapsed={collapsed} />
            ))}
          </div>
        ))}
      </nav>

      <SidebarAccount
        collapsed={collapsed}
        fullName={fullName}
        subtitle={subtitle}
        onSignOut={onSignOut}
        accountMenuExtra={accountMenuExtra}
      />

      {footer}
    </aside>
  );
}
